'use client'

import { useEffect, useState } from 'react'
import { HsTaxListResponse } from '@/modules/hs/types/hs-tax.response'
import { HsTaxHangNgach } from '@/modules/hs/types/hs-response'
import { TaxNote } from '@/modules/hs/types/tax.types'
import HsTaxDetailModal from './HsTaxDetailModal'
import { useParams } from 'next/navigation'

interface HsTaxModalHandlerProps {
  taxList: HsTaxListResponse
  notes: TaxNote[]
  hangngach?: HsTaxHangNgach[]
}

export default function HsTaxModalHandler({
  taxList,
  notes,
  hangngach,
}: HsTaxModalHandlerProps) {
  const [currentHash, setCurrentHash] = useState('')
  const { hsCode } = useParams()

  const handleCloseModal = () => {
    // Xóa hash mà không scroll lên đầu trang
    const url = window.location.pathname + window.location.search
    window.history.replaceState(null, '', url)
    setCurrentHash('')
  }

  useEffect(() => {
    // Hàm cập nhật hash
    const updateHash = () => {
      setCurrentHash(window.location.hash.slice(1))
    }

    // Lấy hash từ URL khi component mount
    updateHash()

    // Lắng nghe thay đổi hash
    window.addEventListener('hashchange', updateHash)

    return () => {
      window.removeEventListener('hashchange', updateHash)
    }
  }, [])

  // Lắng nghe phím ESC để đóng modal
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && currentHash) {
        handleCloseModal()
      }
    }

    if (currentHash) {
      window.addEventListener('keydown', handleEscape)
    }

    return () => {
      window.removeEventListener('keydown', handleEscape)
    }
  }, [currentHash])

  // Xác định loại thuế và dữ liệu dựa trên hash
  const getTaxDataByHash = () => {
    switch (currentHash) {
      case 'nkud':
        return { taxList: taxList.nkud, label: 'Thuế nhập khẩu ưu đãi' }
      case 'bvmt':
        return { taxList: taxList.bvmt, label: 'Thuế bảo vệ môi trường' }
      case 'ttdb':
        return { taxList: taxList.ttdb, label: 'Thuế tiêu thụ đặc biệt' }
      case 'cbpg':
        return { taxList: taxList.cbpg, label: 'Thuế chống bán phá giá' }
      case 'nktt':
        return { taxList: taxList.nktt, label: 'Thuế nhập khẩu thông thường' }
      case 'hangngach':
        return {
          taxList: hangngach || [],
          label: 'Thuế nhập khẩu trong hạn ngạch thuế quan',
        }
      default:
        // Kiểm tra nếu là nkuddb với key cụ thể (vd: nkuddb-ACFTA)
        if (currentHash.startsWith('nkuddb-')) {
          const key = currentHash.replace('nkuddb-', '')
          const data = taxList.nkuddb[key]
          if (data) {
            return {
              taxList: data,
              label: `Thuế nhập khẩu ưu đãi đặc biệt - ${key}`,
            }
          }
        }
        return null
    }
  }

  const taxData = getTaxDataByHash()
  const isOpen = !!currentHash && !!taxData
  return (
    <HsTaxDetailModal
      isOpen={isOpen}
      onClose={handleCloseModal}
      taxList={taxData?.taxList || []}
      label={taxData?.label || ''}
      hsCode={hsCode as string}
      notes={notes}
    />
  )
}
